import { useEffect, useRef } from 'react'
import { SegmentedProps } from './props.js'

/**
 * Always capped to its container's width and scrollable inside itself. Left
 * uncapped, an `inline-flex` pill bar with many options is as wide as all of
 * them side by side — on a phone that widened the whole page, so every swipe
 * to see the last tab dragged the entire screen sideways. When the options
 * fit (desktop) nothing changes: it is exactly as wide as its content.
 */
export function Segmented<T extends string = string>({
  options,
  value,
  onChange,
  label,
  wrap = false,
  className = '',
}: SegmentedProps<T>) {
  const activeRef = useRef<HTMLButtonElement | null>(null)

  // Keep the selected option visible when it is off-screen inside the bar
  // (e.g. arriving on "Paid" or "Cancelled" on a phone). `block: 'nearest'`
  // so this only ever scrolls the bar sideways, never the page vertically.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' })
  }, [value])

  return (
    <div
      role="tablist"
      aria-label={label}
      className={[
        'inline-flex items-center gap-1 max-w-full min-w-0',
        'bg-surface-container border border-outline-variant p-1',
        wrap
          ? 'flex-wrap rounded-2xl'
          : 'rounded-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {options.map((option) => (
        <button
          key={option.value}
          ref={value === option.value ? activeRef : undefined}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          onClick={() => onChange(option.value)}
          className={[
            'rounded-full px-3 h-7 text-label-lg whitespace-nowrap interactive flex-shrink-0',
            value === option.value
              ? 'bg-surface-highest text-on-surface'
              : 'text-on-surface-variant hover:text-on-surface',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
